import DashboardIcon from '@mui/icons-material/Dashboard';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SettingsIcon from '@mui/icons-material/Settings';
import ShieldIcon from '@mui/icons-material/Shield';
import { Box, Card, Grid2, IconButton } from '@mui/material';
import { grey } from '@mui/material/colors';
import logo from 'assets/images/logoProdutos/alertai_icon_light.svg';
import TooltipComponent from 'components/TooltipComponent';
import { SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStreamByEndpointAsync } from 'services/Events';
import { transformDateStringInDate } from 'utils/TransformUtils';
import LastEvent from './_components/LastEvent';
import MonitoringOptionsComponent from './_components/MonitoringOptions';
import MonitoringOptionsNotificationComponent from './_components/MonitoringOptionsNotification';
import DashboardMonitoringPage from './layouts/DashboardMonitoringLayout';
import DefenseMonitoringLayout from './layouts/DefenseMonitoringLayout';
import ListMonitoringPage from './layouts/ListMonitoringLayout';
import PanicAlarmCentralPopupComponent from 'components/PanicAlarmCentralPopupComponent';

const EVENT_LASTEVENT_ENDPOINT = 'v1/events/last';

const MonitoringPage = () => {
  const { t } = useTranslation();
  const [selectedDashboard, setSelectedDashboard] = useState('dashboard');
  const [isVisibleEditionDialog, setIsVisibleEditionDialog] =
    useState<boolean>(false);
  const [isVisibleNotificationDialog, setIsVisibleNotificationDialog] =
    useState<boolean>(false);
  const [lastEvent, setLastEvent] = useState();
  const [isOpenImageModal, setIsOpenImageModal] = useState<boolean>(false);
  const [imageUrlToModal, setImageUrlToModal] = useState<string>('');
  const [events, setEvents] = useState<any[]>([]);

  useEffect(() => {
    getStreamByEndpointAsync(EVENT_LASTEVENT_ENDPOINT, setEvents);
  }, []);

  const handleDashboardChange = (type: SetStateAction<string>) => {
    setSelectedDashboard(type);
  };

  const handleClickImage = (imageUrl: string) => {
    setImageUrlToModal(imageUrl);
    setIsOpenImageModal(true);
  };

  useEffect(() => {
    if (events.length > 0) {
      const mostRecentEvent = events.reduce((latest, current) =>
        transformDateStringInDate(current.createdAt) >
        transformDateStringInDate(latest.createdAt)
          ? current
          : latest,
      );
      setLastEvent(mostRecentEvent);
    }
  }, [events]);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          backgroundColor: 'transparent',
          p: 0,
          m: 0,
          gap: 0,
          justifyContent: 'flex-start',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: { xs: '100%' },
            height: '100%',
            maxWidth: '65%',
            p: 0,
            m: 0,
          }}
        >
          {selectedDashboard === 'dashboard' && (
            <DashboardMonitoringPage events={events} />
          )}
          {selectedDashboard === 'list' && <ListMonitoringPage />}
          {selectedDashboard === 'defense' && <DefenseMonitoringLayout />}
        </Box>
        <Grid2
          sx={{
            width: { xs: '100%' },
            maxWidth: '35%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            p: 0,
            m: 0,
          }}
        >
          <Card
            sx={{
              width: '100%',
              minWidth: 0,
              height: '95%',
              backgroundColor: '#202940',
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
              p: 2,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box display={'flex'} justifyItems={'center'} marginTop={4}>
              <img
                key="logo-expanded"
                height={50}
                src={logo}
                style={{ maxWidth: '100%' }}
              />
            </Box>

            <Box
              display={'flex'}
              flexDirection={'row'}
              marginTop={2}
              justifyContent={'center'}
              flexWrap="wrap"
              gap={0.5}
            >
              <IconButton onClick={() => handleDashboardChange('dashboard')}>
                <TooltipComponent title={t('dashboard')}>
                  <DashboardIcon sx={{ color: grey[50] }} />
                </TooltipComponent>
              </IconButton>
              <IconButton onClick={() => handleDashboardChange('list')}>
                <TooltipComponent title={t('list')}>
                  <FormatListBulletedIcon sx={{ color: grey[50] }} />
                </TooltipComponent>
              </IconButton>
              <IconButton onClick={() => handleDashboardChange('defense')}>
                <TooltipComponent title={t('defense')}>
                  <ShieldIcon sx={{ color: grey[50] }} />
                </TooltipComponent>
              </IconButton>
              <IconButton onClick={() => setIsVisibleNotificationDialog(true)}>
                <TooltipComponent title={t('notifications')}>
                  <NotificationsActiveIcon sx={{ color: grey[50] }} />
                </TooltipComponent>
              </IconButton>
              <IconButton onClick={() => setIsVisibleEditionDialog(true)}>
                <TooltipComponent title={t('settings')}>
                  <SettingsIcon sx={{ color: grey[50] }} />
                </TooltipComponent>
              </IconButton>
            </Box>
            <Box sx={{ width: '100%', mt: 5, height: '70%' }}>
              <LastEvent
                event={lastEvent}
                onClickInImage={handleClickImage}
                title={t('lastEvent')}
              />
            </Box>
          </Card>
        </Grid2>
      </Box>

      <MonitoringOptionsNotificationComponent
        isOpen={isVisibleNotificationDialog}
        setIsOpen={setIsVisibleNotificationDialog}
      />

      <MonitoringOptionsComponent
        isOpen={isVisibleEditionDialog}
        setIsOpen={setIsVisibleEditionDialog}
      />

      <PanicAlarmCentralPopupComponent />
    </>
  );
};

export default MonitoringPage;
